import * as v from 'valibot';
import {
  CompletionStatusSchema as SharedCompletionStatusSchema,
  EventSchema as SharedEventSchema,
  MaterialSchema as SharedMaterialSchema,
  ProgressTrackingTypeSchema as SharedProgressTrackingTypeSchema,
  SuccessStatusSchema as SharedSuccessStatusSchema,
} from '@fe-learner-common';

const SuccessStatusSchema = { ...SharedSuccessStatusSchema };
const CompletionStatusSchema = { ...SharedCompletionStatusSchema };
const ProgressTrackingTypeSchema = { ...SharedProgressTrackingTypeSchema };
const EventSchema = { ...SharedEventSchema };
const MaterialSchema = { ...SharedMaterialSchema };
const ComponentType = v.union([
  v.literal('material-genericfile'),
  v.literal('material-scorequiz'),
  v.literal('material-quiz'),
  v.literal('material-htmlsnippet'),
  v.literal('material-challenge'),
  v.literal('material-external'),
  v.literal('material-crisp'),
  v.literal('material-linkedsubproject'),
  v.literal('material-audioplayer'),
  v.literal('material-website'),
  v.literal('richtext'),
  v.literal('upload'),
  v.literal('postonwall'),

  // events
  v.literal('ev_classroom'),
  v.literal('ev_virtual'),
  v.literal('ev_call'),

  // video types
  v.literal('material-oembedvideo'),
  v.literal('material-poembedvideo'),
  v.literal('material-wevideo'),
  v.literal('material-youtubevideo'),
  v.literal('material-diplomavideo'),
  v.literal('material-vimeovideo'),
  v.literal('material-vimeo2video'),
  v.literal('material-brightcovevideo'),
  // image types
  v.literal('material-image'),
  v.literal('material-unsplashimage'),

  // lti types
  v.literal('material-lti1v3resource'),

  // bx
  v.literal('material-mobileapp'),

  // known legacy types
  v.literal('ev_webex'),
  v.literal('ev_webinar'),
  v.literal('ev_email'),
  v.literal('ev_offline'),

  // fallthrough
  v.string(),
]);
const ResourceSchema = v.object({
  referenceType: v.string(),
  resource: v.nullish(v.string()),
  id: v.nullish(v.string()),
  type: v.nullish(v.string()),
});

const ComponentUploadSchema = v.object({
  uuid: v.nullish(v.string()),
  comment: v.nullish(v.string()),
  crl: v.nullish(v.string()),
  created: v.nullish(v.union([v.string(), v.number()])),

  filename: v.string(),
  stateId: v.nullish(v.string()),
});

const ComponentPropertiesSchema = v.object(
  {
    position: v.union([v.literal('A'), v.literal('B'), v.literal('C')]),
  },
  v.string(),
);

const ComponentCommentSchema = v.object({
  created: v.string(),
  createdBy: v.number(),
  text: v.string(),
  uuid: v.string(),
});

export const ComponentSchema = v.object({
  description: v.nullish(v.string()),
  mandatory: v.boolean(),
  successStatus: v.nullish(SuccessStatusSchema),
  completionStatus: CompletionStatusSchema,
  basetype: v.nullish(v.string()),
  subtype: v.nullish(v.string()),
  dueDateInfo: v.object({
    dueDate: v.nullish(v.string()),
    overdue: v.boolean(),
    dueDateAvailable: v.boolean(),
  }),
  imgAlt: v.nullish(v.string()),
  material: v.nullish(MaterialSchema),
  progressTrackingType: ProgressTrackingTypeSchema,
  completed: v.nullish(v.boolean()),
  completedDate: v.nullish(v.number()),
  completeByDate: v.nullish(v.string()),
  cid: v.string(),
  name: v.nullish(v.string()),
  resources: v.array(ResourceSchema),
  properties: ComponentPropertiesSchema,
  type: v.string(),
  componentType: ComponentType,
  link: v.nullish(v.string()),
  useMode: v.nullish(v.string()),
  evaluation: v.nullish(v.boolean()),
  uploads: v.nullish(v.array(ComponentUploadSchema)),
  userComments: v.nullish(v.array(ComponentCommentSchema)),
  trainerComments: v.nullish(v.array(ComponentCommentSchema)),
  textBlocks: v.nullish(v.array(v.object({
    stateId: v.string(),
    cid: v.string(),
    created: v.number(),
    createdBy: v.number(),
    text: v.string(),
  }))),
  googleMaps: v.nullish(v.boolean()),
  location: v.nullish(v.string()),
  locationUrl: v.nullish(v.string()),
  locationUrlExtra: v.nullish(v.string()),
  starts: v.nullish(v.number()),
  ends: v.nullish(v.number()),
  displayCompletion: v.nullish(v.string()),
  event: v.nullish(EventSchema),
  url: v.nullish(v.string()),
  phone: v.nullish(v.string()),
  code: v.nullish(v.string()),
});
export type TComponent = v.Output<typeof ComponentSchema>;
export type TComponentType = v.Output<typeof ComponentType>;
export type TComponentUpload = v.Output<typeof ComponentUploadSchema>;
